import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { validarSize } from 'src/app/common/validation-utils';
import {
  CumplimientoFiltro,
  ProgramacionPendiente,
  CumplimientoRequest,
  CumplimientoResponse,
  Cumplimiento,
  SaldoResponse,
  ConfiguracionCumplimientoResponse,
  CursorPageResponse,
  EnvioInformeRequest,
  EnvioInformeResultado,
  InformePendiente,
  HistorialCumplimiento,
  ActaCumplimiento,
  AsociarActaResponse,
  DatosActaRequest,
  GenerarActaResponse,
  RiesgoPotencialFiltro,
  RiesgoPotencialConteo,
  RiesgoPotencialPage,
  ActualizarRiesgoPotencialRequest,
  RiesgoPotencialDetalle,
  RiesgoSeguimiento,
  GuardarRiesgoSeguimientoRequest,
  EvidenciaSeguimientoResponse,
  CursorDireccion
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import { esJustificacionRequerida } from 'src/app/common/http-error-utils';
import { JustificacionTardiaRechazoStateService } from 'src/app/infrastructure/state/justificacion-tardia-rechazo-state.service';

/**
 * Adaptador HTTP: HttpCumplimientoRepository
 *
 * Implementa el puerto CumplimientoRepository para comunicación HTTP con backend.
 * Realiza llamadas REST al backend Spring Boot (CumplimientoController).
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto CumplimientoRepository del dominio
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpCumplimientoRepository implements CumplimientoRepository {

  private readonly http = inject(HttpClient);
  private readonly rechazoJustificacion = inject(JustificacionTardiaRechazoStateService);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/orientacionesProgramadas`;


  // === Programaciones Pendientes ===

  /**
   * Lista programaciones pendientes de cumplimiento con filtros y paginación keyset.
   *
   * GET /api/servicios/orientacionesProgramadas/programados
   *
   * @param filtro Filtros de búsqueda (todos opcionales)
   * @param cursor Cursor opaco devuelto por el backend; null para la primera página y para LAST
   * @param size Tamaño de página (default: 10)
   * @param direction NEXT | PREVIOUS | LAST (frontend-girop §17.7: la última página se pide con LAST)
   */
  async listarProgramacionesPendientes(
    filtro: CumplimientoFiltro,
    cursor: string | null,
    size: number = 10,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST' = 'NEXT'
  ): Promise<CursorPageResponse<ProgramacionPendiente>> {
    validarSize(size);
    let params = new HttpParams()
      .set('size', size.toString())
      .set('direction', direction);

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    // Agregar filtros opcionales - Legacy: Todos EQUAL (no LIKE), usando nmconsecutivo (FK)
    if (filtro.ordenServicio) {
      params = params.set('ordenServicio', filtro.ordenServicio);
    }
    if (filtro.cdoficina) {
      params = params.set('cdoficina', filtro.cdoficina);
    }
    // Legacy: cdunidad_administrativa (código STRING, no nmconsecutivo)
    if (filtro.cdunidadAdministrativa) {
      params = params.set('cdunidadAdministrativa', filtro.cdunidadAdministrativa);
    }
    // Legacy: nmconsecutivo_cliente.nmconsecutivo
    if (filtro.nmconsecutivoCliente) {
      params = params.set('nmconsecutivoCliente', filtro.nmconsecutivoCliente.toString());
    }
    if (filtro.tipoDocumentoCliente) {
      params = params.set('tipoDocumentoCliente', filtro.tipoDocumentoCliente);
    }
    // Legacy: nmconsecutivo_asesor.nmconsecutivo
    if (filtro.nmconsecutivoAsesor) {
      params = params.set('nmconsecutivoAsesor', filtro.nmconsecutivoAsesor.toString());
    }
    // Filtro por CDPERSONA del asesor (búsqueda desde dialog)
    if (filtro.cdpersonaAsesor) {
      params = params.set('cdpersonaAsesor', filtro.cdpersonaAsesor.toString());
    }
    if (filtro.tipoDocumentoAsesor) {
      params = params.set('tipoDocumentoAsesor', filtro.tipoDocumentoAsesor);
    }
    // Legacy: nmconsecutivo_beneficiario.nmconsecutivo
    if (filtro.nmconsecutivoBeneficiario) {
      params = params.set('nmconsecutivoBeneficiario', filtro.nmconsecutivoBeneficiario.toString());
    }
    if (filtro.tipoDocumentoBeneficiario) {
      params = params.set('tipoDocumentoBeneficiario', filtro.tipoDocumentoBeneficiario);
    }
    if (filtro.fechaInicial) {
      params = params.set('fechaInicial', filtro.fechaInicial);
    }
    if (filtro.fechaFinal) {
      params = params.set('fechaFinal', filtro.fechaFinal);
    }
    // Legacy: nmconsec_homologacion_servicio.nmconsecutivo
    if (filtro.nmconsecutivoHomologacion) {
      params = params.set('nmconsecutivoHomologacion', filtro.nmconsecutivoHomologacion.toString());
    }
    // Legacy: nmconsec_benef_ct.nmconsecutivo
    if (filtro.nmconsecutivoCentroTrabajo) {
      params = params.set('nmconsecutivoCentroTrabajo', filtro.nmconsecutivoCentroTrabajo.toString());
    }

    return firstValueFrom(
      this.http.get<CursorPageResponse<ProgramacionPendiente>>(
        `${this.baseUrl}/programados`,
        { params }
      )
    );
  }

  /**
   * Obtiene el detalle de una programación pendiente.
   *
   * GET /api/cumplimientos/programados/{id}
   *
   * @param nmconsecutivo ID de la programación
   */
  async obtenerProgramacion(nmconsecutivo: number): Promise<ProgramacionPendiente> {
    return firstValueFrom(
      this.http.get<ProgramacionPendiente>(
        `${this.baseUrl}/programados/${nmconsecutivo}`
      )
    );
  }

  // === CRUD Cumplimientos ===

  /**
   * Registra un nuevo cumplimiento de servicio.
   *
   * POST /api/cumplimientos
   *
   * @param request Datos del cumplimiento
   */
  async registrarCumplimiento(request: CumplimientoRequest): Promise<CumplimientoResponse> {
    try {
      return await firstValueFrom(
        this.http.post<CumplimientoResponse>(this.baseUrl, request)
      );
    } catch (error) {
      // El backend exige justificación (cumplimiento tardío) que el formulario no pidió:
      // se avisa para que el formulario abra el modal. El error se relanza igual, de modo que
      // cada pantalla conserva su manejo habitual (toast, formulario abierto).
      if (esJustificacionRequerida(error)) {
        this.rechazoJustificacion.notificar();
      }
      throw error;
    }
  }

  /**
   * Obtiene el detalle de un cumplimiento registrado.
   *
   * GET /api/cumplimientos/{id}
   *
   * @param nmconsecutivo ID del cumplimiento
   */
  async obtenerCumplimiento(nmconsecutivo: number): Promise<CumplimientoResponse> {
    return firstValueFrom(
      this.http.get<CumplimientoResponse>(
        `${this.baseUrl}/${nmconsecutivo}`
      )
    );
  }

  /**
   * Lista los cumplimientos de una programación.
   *
   * GET /api/cumplimientos/programados/{id}/cumplimientos
   *
   * @param nmconsecutivoProgramacion ID de la programación
   */
  async listarCumplimientosProgramacion(nmconsecutivoProgramacion: number): Promise<Cumplimiento[]> {
    return firstValueFrom(
      this.http.get<Cumplimiento[]>(
        `${this.baseUrl}/programados/${nmconsecutivoProgramacion}/cumplimientos`
      )
    );
  }

  /**
   * Actualiza nombre y correo del contacto de un cumplimiento.
   *
   * PUT /api/servicios/ordenes-servicio/cumplimientos/{id}/contacto
   */
  async actualizarContactoCumplimiento(
    nmconsecutivoCumplimiento: number,
    nombreContacto: string,
    emailContacto: string
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<boolean>(
        `${environment.backend.baseUrl}/servicios/ordenes-servicio/cumplimientos/${nmconsecutivoCumplimiento}/contacto`,
        { nombreContacto, emailContacto }
      )
    );
  }

  // === Saldo de Orden ===

  /**
   * Obtiene el saldo por ejecutar de una orden de servicio.
   *
   * GET /api/cumplimientos/ordenes/{id}/saldo
   *
   * @param nmconsecOrdenServDetalle ID del detalle de orden de servicio
   */
  async obtenerSaldoOrden(nmconsecOrdenServDetalle: number): Promise<SaldoResponse> {
    return firstValueFrom(
      this.http.get<SaldoResponse>(
        `${this.baseUrl}/ordenes/${nmconsecOrdenServDetalle}/saldo`
      )
    );
  }

  async obtenerConfiguracionCumplimiento(): Promise<ConfiguracionCumplimientoResponse> {
    return firstValueFrom(
      this.http.get<ConfiguracionCumplimientoResponse>(
        `${this.baseUrl}/configuracion`
      )
    );
  }

  // === Envío de Informes (P8/FileNet) ===

  /**
   * Lista los cumplimientos pendientes de envío de informe.
   *
   * GET /api/servicios/informes-cumplimiento
   *
   * @param filtros Filtros opcionales de búsqueda
   */
  async listarInformesPendientes(filtros?: Record<string, string>): Promise<InformePendiente[]> {
    let params = new HttpParams();

    if (filtros) {
      Object.entries(filtros).forEach(([key, value]) => {
        if (value) {
          params = params.set(key, value);
        }
      });
    }

    return firstValueFrom(
      this.http.get<InformePendiente[]>(
        `${environment.backend.baseUrl}/servicios/informes-cumplimiento`,
        { params }
      )
    );
  }

  /**
   * Envía un informe con documento adjunto a P8/FileNet.
   *
   * POST /api/servicios/informes-cumplimiento/enviar (multipart/form-data)
   *
   * El backend (InformeCumplimientoController) espera:
   * - archivo: File (multipart)
   * - tipoInforme: P/F
   * - informes: string JSON array de nmconsecutivo
   *
   * @param request Datos del informe y archivo en Base64
   */
  async enviarInforme(request: EnvioInformeRequest): Promise<EnvioInformeResultado> {
    // Convertir Base64 a Blob para multipart
    const byteCharacters = atob(request.archivoContenido);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: request.archivoContentType });

    // Construir FormData
    const formData = new FormData();
    formData.append('archivo', blob, request.archivoNombre);
    formData.append('tipoInforme', request.tipoInforme);
    formData.append('informes', JSON.stringify(request.informes));

    return firstValueFrom(
      this.http.post<EnvioInformeResultado>(
        `${environment.backend.baseUrl}/servicios/informes-cumplimiento/enviar`,
        formData
      )
    );
  }
  

  // === Riesgo Potencial ===

  async contarRiesgosPotenciales(filtro: RiesgoPotencialFiltro): Promise<RiesgoPotencialConteo> {
    let params = new HttpParams();

    if (filtro.nmconsecutivoOficina != null) {
      params = params.set('nmconsecutivoOficina', filtro.nmconsecutivoOficina.toString());
    }
    if (filtro.nmconsecutivoAsesor != null) {
      params = params.set('nmconsecutivoAsesor', filtro.nmconsecutivoAsesor.toString());
    }
    if (filtro.nmconsecutivoBenef != null) {
      params = params.set('nmconsecutivoBenef', filtro.nmconsecutivoBenef.toString());
    }
    if (filtro.nmconsecutivoCumpl != null) {
      params = params.set('nmconsecutivoCumpl', filtro.nmconsecutivoCumpl.toString());
    }
    if (filtro.nmtipoSituacion != null) {
      params = params.set('nmtipoSituacion', filtro.nmtipoSituacion.toString());
    }
    if (filtro.cdestado) {
      params = params.set('cdestado', filtro.cdestado);
    }
    if (filtro.fechaIdentificacionInicial) {
      params = params.set('fechaIdentificacionInicial', filtro.fechaIdentificacionInicial);
    }
    if (filtro.fechaIdentificacionFinal) {
      params = params.set('fechaIdentificacionFinal', filtro.fechaIdentificacionFinal);
    }
    if (filtro.fechaSeguimientoInicial) {
      params = params.set('fechaSeguimientoInicial', filtro.fechaSeguimientoInicial);
    }
    if (filtro.fechaSeguimientoFinal) {
      params = params.set('fechaSeguimientoFinal', filtro.fechaSeguimientoFinal);
    }

    return firstValueFrom(
      this.http.get<RiesgoPotencialConteo>(
        `${this.baseUrl}/riesgos-potenciales/conteo`,
        { params }
      )
    );
  }

  private riesgoFiltroParams(filtro: RiesgoPotencialFiltro): HttpParams {
    let params = new HttpParams();

    const numericos: Array<[string, number | undefined]> = [
      ['nmconsecutivo', filtro.nmconsecutivo],
      ['nmconsecutivoOficina', filtro.nmconsecutivoOficina],
      ['nmconsecutivoAsesor', filtro.nmconsecutivoAsesor],
      ['nmconsecutivoBenef', filtro.nmconsecutivoBenef],
      ['nmconsecutivoCumpl', filtro.nmconsecutivoCumpl],
      ['nmconsecRiesgo', filtro.nmconsecRiesgo],
      ['nmtipoSituacion', filtro.nmtipoSituacion]
    ];
    for (const [nombre, valor] of numericos) {
      if (valor != null) {
        params = params.set(nombre, valor.toString());
      }
    }

    const textos: Array<[string, string | undefined]> = [
      ['dsorden', filtro.dsorden],
      ['dniProfesional', filtro.dniProfesional],
      ['cdestado', filtro.cdestado],
      ['dniBeneficiario', filtro.dniBeneficiario],
      ['fechaIdentificacionInicial', filtro.fechaIdentificacionInicial],
      ['fechaIdentificacionFinal', filtro.fechaIdentificacionFinal],
      ['fechaSeguimientoInicial', filtro.fechaSeguimientoInicial],
      ['fechaSeguimientoFinal', filtro.fechaSeguimientoFinal]
    ];
    for (const [nombre, valor] of textos) {
      if (valor) {
        params = params.set(nombre, valor);
      }
    }

    for (const codigo of filtro.cdestados ?? []) {
      params = params.append('cdestados', codigo);
    }

    return params;
  }

  async listarRiesgosPotenciales(
    filtro: RiesgoPotencialFiltro,
    size: number,
    cursor?: string | null,
    direction: CursorDireccion = 'NEXT'
  ): Promise<RiesgoPotencialPage> {
    // Paginación keyset: el cursor es opaco y lo emite el propio backend en
    // nextCursor/previousCursor. No hay parámetro 'page'.
    let params = this.riesgoFiltroParams(filtro)
      .set('size', size.toString())
      .set('direction', direction);

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return firstValueFrom(
      this.http.get<RiesgoPotencialPage>(`${this.baseUrl}/riesgos-potenciales`, { params })
    );
  }

  /**
   * Actualiza el detalle editable del riesgo potencial.
   * Legacy: comando guardarRiesgoPot («Modificar Detalle»)
   */
  async actualizarRiesgoPotencial(
    idRiesgo: number,
    request: ActualizarRiesgoPotencialRequest
  ): Promise<RiesgoPotencialDetalle> {
    return firstValueFrom(
      this.http.put<RiesgoPotencialDetalle>(
        `${this.baseUrl}/riesgos-potenciales/${idRiesgo}`,
        request
      )
    );
  }

  async obtenerRiesgoPotencial(idRiesgo: number): Promise<RiesgoPotencialDetalle> {
    return firstValueFrom(
      this.http.get<RiesgoPotencialDetalle>(`${this.baseUrl}/riesgos-potenciales/${idRiesgo}`)
    );
  }

  async listarSeguimientosRiesgo(idRiesgo: number): Promise<RiesgoSeguimiento[]> {
    return firstValueFrom(
      this.http.get<RiesgoSeguimiento[]>(`${this.baseUrl}/riesgos-potenciales/${idRiesgo}/seguimientos`)
    );
  }

  async guardarSeguimientoRiesgo(
    idRiesgo: number,
    request: GuardarRiesgoSeguimientoRequest
  ): Promise<RiesgoSeguimiento> {
    return firstValueFrom(
      this.http.post<RiesgoSeguimiento>(
        `${this.baseUrl}/riesgos-potenciales/${idRiesgo}/seguimientos`,
        request
      )
    );
  }

  async cargarEvidenciaSeguimientoRiesgo(
    idRiesgo: number,
    archivoContenido: string,
    archivoNombre: string,
    archivoContentType: string
  ): Promise<EvidenciaSeguimientoResponse> {
    return firstValueFrom(
      this.http.post<EvidenciaSeguimientoResponse>(
        `${this.baseUrl}/riesgos-potenciales/${idRiesgo}/evidencia`,
        { archivoContenido, archivoNombre, archivoContentType }
      )
    );
  }

  // === Historial de Cumplimientos (Legacy: ListarCumplimientosActivity.java) ===
  
  /**
   * Lista los cumplimientos de una programación para el modal de historial.
   * 
   * GET /api/cumplimientos/programados/{id}/cumplimientos
   * 
   * Backend: CumplimientoController.listarCumplimientosProgramacion()
   * Incluye datos de TPSC_PROGRAMAC_SERVICIOS, TPSC_BENEFICIARIOS, TGFC_CUMPLIM_ACTA
   */
  async listarHistorialCumplimientos(nmconsecutivoProgramacion: number): Promise<HistorialCumplimiento[]> {
    return firstValueFrom(
      this.http.get<HistorialCumplimiento[]>(
        `${this.baseUrl}/programados/${nmconsecutivoProgramacion}/cumplimientos`
      )
    );
  }
  
  /**
   * Obtiene la observación de un cumplimiento.
   * 
   * GET /api/cumplimientos/{id}/observacion
   * 
   * Legacy: ListarCumplimientosActivity.getObservacionesCumplimiento()
   */
  async obtenerObservacion(nmconsecutivoCumplimiento: number): Promise<string> {
    return firstValueFrom(
      this.http.get(`${this.baseUrl}/${nmconsecutivoCumplimiento}/observacion`, {
        responseType: 'text'
      })
    );
  }
  
  /**
   * Obtiene la recomendación de un cumplimiento.
   * 
   * GET /api/cumplimientos/{id}/recomendacion
   * 
   * Legacy: ListarCumplimientosActivity.getRecomendacionesCumplimiento()
   */
  async obtenerRecomendacion(nmconsecutivoCumplimiento: number): Promise<string> {
    return firstValueFrom(
      this.http.get(`${this.baseUrl}/${nmconsecutivoCumplimiento}/recomendacion`, {
        responseType: 'text'
      })
    );
  }
  
  /**
   * Obtiene observación y recomendación en una sola llamada.
   * 
   * GET /api/cumplimientos/{id}/obs-rec
   * 
   * Optimización: Una sola llamada HTTP en lugar de 2 separadas.
   */
  async obtenerObservacionYRecomendacion(nmconsecutivoCumplimiento: number): Promise<{observacion: string, recomendacion: string}> {
    return firstValueFrom(
      this.http.get<{observacion: string, recomendacion: string}>(`${this.baseUrl}/${nmconsecutivoCumplimiento}/obs-rec`)
    );
  }
  
  // === Acta (Legacy: ListarCumplimientosActivity.descargarActa360 / asociarActas) ===
  
  /**
   * Busca el acta asociada a un cumplimiento.
   * 
   * GET /api/cumplimientos/{id}/acta
   * 
   * Legacy: AdminCumplimActaHandler.searchActaGirop()
   */
  async buscarActaCumplimiento(nmconsecutivoCumplimiento: number): Promise<ActaCumplimiento | null> {
    try {
      return await firstValueFrom(
        this.http.get<ActaCumplimiento>(`${this.baseUrl}/${nmconsecutivoCumplimiento}/acta`)
      );
    } catch (error: any) {
      if (error?.status === 404) {
        return null;
      }
      throw error;
    }
  }
  
  /**
   * Asocia un acta a un cumplimiento via servicio externo.
   * 
   * POST /api/servicios/ordenes-servicio/programaciones/0/asociar-acta
   * 
   * Legacy: AdminCumplimientoHandler.asociarActasCumplimientos()
   */
  async asociarActa(idCumplimiento: string, consecutivoActa: string): Promise<AsociarActaResponse> {
    return firstValueFrom(
      this.http.post<AsociarActaResponse>(
        `${environment.backend.baseUrl}/servicios/ordenes-servicio/programaciones/0/asociar-acta`,
        { idCumplimiento, consecutivoActa }
      )
    );
  }

  async reintentarGenerarActa(
    nmconsecutivoCumplimiento: number,
    datosActa: DatosActaRequest
  ): Promise<GenerarActaResponse> {
    return firstValueFrom(
      this.http.post<GenerarActaResponse>(
        `${this.baseUrl}/${nmconsecutivoCumplimiento}/generar-acta`,
        datosActa
      )
    );
  }
  
  /**
   * Obtiene la URL para descargar el PDF del acta.
   * 
   * GET /api/cumplimientos/{id}/descargar-acta (returns PDF binary)
   * 
   * Legacy: ConsumeServiceNoReturnAction.descargarActa()
   */
  getDescargarActaUrl(nmconsecutivoCumplimiento: number): string {
    return `${this.baseUrl}/${nmconsecutivoCumplimiento}/descargar-acta`;
  }

  /**
   * Descarga el PDF del acta vía blob y dispara la descarga en el navegador,
   * sin abrir ninguna pestaña externa. Misma UX que DocumentoP8Service.descargar().
   */
  async descargarActaPdf(nmconsecutivoCumplimiento: number, nombreSugerido?: string): Promise<void> {
    const url = this.getDescargarActaUrl(nmconsecutivoCumplimiento);

    try {
      const response = await firstValueFrom(
        this.http.get(url, { responseType: 'blob', observe: 'response' })
      );

      if (!response.body) {
        throw new Error('No se recibió contenido del servidor');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let nombreArchivo = nombreSugerido || 'acta.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          nombreArchivo = match[1];
        }
      }

      const blobUrl = window.URL.createObjectURL(response.body);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = nombreArchivo;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) {
          throw new Error('No se encontró un acta disponible para descargar');
        }
        if (error.status === 401 || error.status === 403) {
          throw new Error('No tiene permisos para acceder al documento');
        }
      }
      throw new Error('Ocurrió un error al descargar el acta. Por favor intente nuevamente.');
    }
  }
}


